import React, { useState, useEffect, useRef, useCallback } from 'react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { Landmark, Loader2, Sparkles, ChevronDown, ChevronRight, ExternalLink, Trash2, RotateCw, Search, Link2, Building2, Wrench, Puzzle, Database } from 'lucide-react';
import { filterSummary } from './DealFilterPanel';
import { CompanyRow } from './ThesisCompanyRow';
import { useAuth } from '../contexts/AuthContext';
import { canSeeInfraFunds, canManageInfraFunds } from '../lib/access';

export const ANGLE_STYLE = {
  core: { label: 'Kern-Infrastruktur', cls: 'text-emerald-300 border-emerald-500/40 bg-emerald-500/10', Icon: Building2 },
  adjacent: { label: 'Angrenzend', cls: 'text-sky-300 border-sky-500/40 bg-sky-500/10', Icon: Wrench },
  add_on: { label: 'Add-on', cls: 'text-amber-300 border-amber-500/40 bg-amber-500/10', Icon: Puzzle },
};
const fmtTs = (iso) => iso ? new Date(iso).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '';
// Note handed to the deal page: replaces the thesis note when the deal is approved from Schritt 4
export const fundNoteFor = (fundName, r) => {
  const angle = ANGLE_STYLE[r.fund_angle]?.label || '';
  const head = [angle, r.fund_angle_label].filter(Boolean).join(' · ');
  const link = r.fund_portfolio_link ? ` (passt zu ${r.fund_portfolio_link})` : '';
  return `Infra-Fonds-Screening „${fundName || ''}“ — ${head}${link}: ${r.fund_reason || ''}`.trim().slice(0, 1500);
};
const PHASE_TEXT = {
  searching: 'Web-Recherche läuft (Strategie · Portfolio · angrenzende Investments)…',
  crawling: 'Fonds-Website wird gelesen…',
  profiling: 'Investment-Profil wird erstellt…',
  matching: 'Datenbank wird nach passenden Firmen durchsucht…',
  scoring: 'Kandidaten werden bewertet…',
};

export function AngleBadge({ angle, label, link }) {
  const st = ANGLE_STYLE[angle];
  if (!st) return null;
  const Icon = st.Icon;
  return (
    <span className="inline-flex items-center gap-1.5 flex-wrap" data-testid="fund-angle-badge">
      <span className={`inline-flex items-center gap-1 text-[10px] font-medium border rounded-sm px-1.5 py-0.5 ${st.cls}`}>
        <Icon className="w-3 h-3" /> {st.label}{label ? ` · ${label}` : ''}
      </span>
      {link && <span className="text-[10px] text-amber-300/80 inline-flex items-center gap-1"><Link2 className="w-3 h-3" /> passt zu {link}</span>}
    </span>
  );
}

export function FundProfile({ fund, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  const p = fund.profile;
  if (!p) return null;
  const st = p.strategy || {};
  return (
    <div className="border border-border/60 rounded-sm bg-secondary/20" data-testid="fund-profile">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center gap-2 px-3 py-2 text-left" data-testid="fund-profile-toggle">
        <ChevronDown className={`w-3.5 h-3.5 text-muted-foreground transition-transform ${open ? 'rotate-180' : ''}`} />
        <span className="text-xs font-medium text-foreground">Investment-Profil: {fund.name}</span>
        <span className="text-[10px] text-muted-foreground ml-auto">
          {p.core_targets?.length || 0} Kern · {p.adjacent_angles?.length || 0} angrenzend · {p.portfolio_examples?.length || 0} Portfolio
          {fund.cost_usd != null && ` · Recherche $${Number(fund.cost_usd).toFixed(2)}`}
        </span>
      </button>
      {open && (
        <div className="px-3 pb-3 space-y-3 text-xs">
          <p className="text-foreground/90 leading-relaxed" data-testid="fund-summary">{p.summary}</p>
          {(st.regions || st.ticket_size || st.dach_activity || st.style) && (
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-muted-foreground">
              {st.regions && <span><span className="text-foreground/60">Regionen:</span> {st.regions}</span>}
              {st.ticket_size && <span><span className="text-foreground/60">Ticket:</span> {st.ticket_size}</span>}
              {st.dach_activity && <span><span className="text-foreground/60">DACH:</span> {st.dach_activity}</span>}
              {st.style && <span><span className="text-foreground/60">Stil:</span> {st.style}</span>}
            </div>
          )}
          {fund.research_warning && <p className="text-[11px] text-amber-300/90">{fund.research_warning}</p>}
          {fund.crawl_error && <p className="text-[11px] text-amber-300/70">{fund.crawl_error}</p>}
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <p className="text-[10px] uppercase tracking-widest font-bold text-emerald-300/80 mb-1.5 inline-flex items-center gap-1"><Building2 className="w-3 h-3" /> Kern-Infrastruktur (was der Fonds direkt kauft)</p>
              <ul className="space-y-1">
                {(p.core_targets || []).map((t, i) => (
                  <li key={i} className="text-[11px]" data-testid={`fund-core-${i}`}><span className="text-foreground font-medium">{t.label}</span>{t.description && <span className="text-muted-foreground"> — {t.description}</span>}</li>
                ))}
              </ul>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-widest font-bold text-sky-300/80 mb-1.5 inline-flex items-center gap-1"><Wrench className="w-3 h-3" /> Angrenzende Geschäftsmodelle (Angles für unsere Datenbank)</p>
              <ul className="space-y-1">
                {(p.adjacent_angles || []).map((t, i) => (
                  <li key={i} className="text-[11px]" data-testid={`fund-adjacent-${i}`}>
                    <span className="text-foreground font-medium">{t.label}</span>{t.description && <span className="text-muted-foreground"> — {t.description}</span>}
                    {t.rationale && <span className="block text-sky-300/70 italic">{t.rationale}</span>}
                  </li>
                ))}
              </ul>
            </div>
          </div>
          {p.portfolio_examples?.length > 0 && (
            <div>
              <p className="text-[10px] uppercase tracking-widest font-bold text-amber-300/80 mb-1.5 inline-flex items-center gap-1"><Puzzle className="w-3 h-3" /> Portfolio-Unternehmen (Add-on-Angles)</p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-1.5">
                {p.portfolio_examples.map((x, i) => (
                  <div key={i} className="border border-border/50 rounded-sm px-2 py-1.5 text-[11px]" data-testid={`fund-portfolio-${i}`}>
                    <span className="text-foreground font-medium">{x.name}</span>{x.country && <span className="text-muted-foreground"> · {x.country}</span>}
                    {x.what && <p className="text-muted-foreground leading-snug">{x.what}</p>}
                    {x.add_on_needs && <p className="text-amber-300/70 leading-snug">sucht: {x.add_on_needs}</p>}
                  </div>
                ))}
              </div>
            </div>
          )}
          {(fund.sources?.length > 0 || fund.site_pages?.length > 0) && (
            <details className="text-[11px]">
              <summary className="cursor-pointer text-muted-foreground hover:text-foreground">Quellen ({(fund.sources || []).length} Web · {(fund.site_pages || []).length} Website-Seiten)</summary>
              <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
                {[...(fund.site_pages || []), ...(fund.sources || [])].map((u, i) => (
                  <a key={i} href={u} target="_blank" rel="noopener noreferrer" className="text-sky-400/80 hover:text-sky-300 inline-flex items-center gap-1 truncate max-w-[260px]">
                    <ExternalLink className="w-2.5 h-2.5 shrink-0" /> {u.replace(/^https?:\/\/(www\.)?/, '')}
                  </a>
                ))}
              </div>
            </details>
          )}
        </div>
      )}
    </div>
  );
}

export function FundScreenerCard({ filters, activeFilters, ownerOptions, rowProps }) {
  const { user } = useAuth();
  const infraAccess = canSeeInfraFunds(user);  // read + screen existing funds
  const canManage = canManageInfraFunds(user);  // create / research / delete (costs)
  const [funds, setFunds] = useState([]);
  const [hasPplx, setHasPplx] = useState(true);
  const [name, setName] = useState('');
  const [website, setWebsite] = useState('');
  const [starting, setStarting] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const pollRef = useRef(null);

  const loadList = useCallback(async () => {
    try { const r = await api.fundList({ status: 'profiled', limit: 200 }); setFunds(r.data.funds || []); setHasPplx(!!r.data.perplexity); } catch { /* noop */ }
  }, []);

  const loadDetail = useCallback(async (id) => {
    try {
      const r = await api.fundGet(id);
      setDetail(r.data);
      const busy = r.data.fund.status === 'researching' || r.data.run?.status === 'running';
      if (!busy) { clearInterval(pollRef.current); pollRef.current = null; loadList(); }
      return r.data;
    } catch { clearInterval(pollRef.current); pollRef.current = null; return null; }
  }, [loadList]);

  const startPolling = useCallback((id) => {
    clearInterval(pollRef.current);
    pollRef.current = setInterval(() => loadDetail(id), 4000);
  }, [loadDetail]);

  useEffect(() => {
    loadList().then(() => {});
    return () => clearInterval(pollRef.current);
  }, [loadList]);

  useEffect(() => {
    // resume polling for a fund that is still researching/screening (e.g. after navigating back)
    const busy = funds.find(f => f.status === 'researching');
    if (busy && !selectedId) { setSelectedId(busy.id); }
  }, [funds, selectedId]);

  useEffect(() => {
    if (!selectedId) { setDetail(null); return; }
    loadDetail(selectedId).then(d => {
      if (d && (d.fund.status === 'researching' || d.run?.status === 'running')) startPolling(selectedId);
    });
    return () => clearInterval(pollRef.current);
  }, [selectedId, loadDetail, startPolling]);

  const start = async () => {
    if (name.trim().length < 3) { toast.error('Bitte den Namen des Fonds eingeben'); return; }
    setStarting(true);
    try {
      const r = await api.fundCreate(name.trim(), website.trim(), filters);
      toast.success(`Recherche zu „${r.data.name}“ gestartet — Strategie, Portfolio und angrenzende Investments werden analysiert`);
      setName(''); setWebsite('');
      await loadList();
      setSelectedId(r.data.id);
      startPolling(r.data.id);
    } catch (e) { toast.error(e.response?.data?.detail || 'Start fehlgeschlagen'); }
    setStarting(false);
  };

  const rescreen = async (id) => {
    try {
      await api.fundScreen(id, filters);
      toast.success(activeFilters.length ? `Screening gestartet — innerhalb deiner ${activeFilters.length} Filter` : 'Screening gestartet — Succession Possible + OLTA TAM');
      setSelectedId(id); startPolling(id); loadDetail(id);
    } catch (e) { toast.error(e.response?.data?.detail || 'Screening fehlgeschlagen'); }
  };

  const reresearch = async (f) => {
    try {
      await api.fundResearch(f.id, f.website, filters);
      toast.success('Recherche wird aktualisiert');
      setSelectedId(f.id); startPolling(f.id); loadDetail(f.id);
    } catch (e) { toast.error(e.response?.data?.detail || 'Recherche fehlgeschlagen'); }
  };

  const remove = async (f) => {
    setConfirmDel(null);
    try {
      await api.fundDelete(f.id);
      if (selectedId === f.id) setSelectedId(null);
      loadList();
      toast.success('Fonds gelöscht');
    } catch { toast.error('Löschen fehlgeschlagen'); }
  };

  const fund = detail?.fund;
  const run = detail?.run;
  const rows = detail?.rows || [];
  const busy = fund?.status === 'researching' || run?.status === 'running';
  const phase = fund?.status === 'researching' ? fund.phase : run?.status === 'running' ? run.phase : null;

  return (
    <div className="space-y-3" data-testid="fund-screener-section">
      <div className="bg-card border border-border rounded-sm p-4 space-y-3">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <p className="text-[10px] uppercase tracking-widest font-bold text-amber-300/80 flex items-center gap-1.5">
              <Landmark className="w-3 h-3" /> Schritt 4 · Infrastructure Fund Screener
            </p>
            <p className="text-[11px] text-muted-foreground mt-1">
              Namen eines Infrastruktur-Fonds / PE-Investors eingeben — die KI recherchiert Strategie, Portfolio und angrenzende Investments
              (Web-Recherche{hasPplx ? '' : ' derzeit ohne Perplexity-Key, nur Website + KI-Wissen'} + Fonds-Website), baut ein Investment-Profil und findet
              passende Firmen in unserer Datenbank — mit Begründung, über welchen Angle sie gefunden wurden.
              {activeFilters.length > 0
                ? <span className="text-amber-300/80" data-testid="fund-filter-hint"> Gescreent wird nur innerhalb deiner {activeFilters.length} Filter aus Schritt 1.</span>
                : <span> Ohne Stage-Filter werden nur Firmen in Succession Possible + OLTA TAM gescreent (keine passed / laufenden Deals).</span>}
            </p>
          </div>
          {infraAccess && (
          <Link to="/infra-funds" data-testid="fund-db-link"
            className="text-[11px] text-amber-300 hover:text-amber-200 inline-flex items-center gap-1 border border-amber-500/30 rounded-sm px-2 h-7 shrink-0">
            <Database className="w-3 h-3" /> Infra-Fonds-Datenbank (Profile & Deals)
          </Link>
          )}
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <input value={name} onChange={e => setName(e.target.value)} placeholder="Fonds / Investor, z. B. Antin Infrastructure Partners"
            data-testid="fund-name-input" disabled={!canManage}
            onKeyDown={e => { if (e.key === 'Enter') start(); }}
            className="flex-1 bg-secondary border border-border rounded-sm px-3 h-9 text-sm text-foreground focus:outline-none focus:border-amber-500/60" />
          <input value={website} onChange={e => setWebsite(e.target.value)} placeholder="Website (optional), z. B. antin-ip.com"
            data-testid="fund-website-input" disabled={!canManage}
            onKeyDown={e => { if (e.key === 'Enter') start(); }}
            className="sm:w-64 bg-secondary border border-border rounded-sm px-3 h-9 text-sm text-foreground focus:outline-none focus:border-amber-500/60" />
          <button onClick={start} disabled={starting || !canManage || funds.some(f => f.status === 'researching')} data-testid="fund-start-btn"
            className="inline-flex items-center justify-center gap-1.5 text-xs font-medium rounded-sm px-4 h-9 bg-amber-600 hover:bg-amber-500 text-white disabled:opacity-50 transition-colors shrink-0">
            {starting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />} Recherchieren & screenen
          </button>
        </div>

        {funds.length > 0 && (
          <div className="border-t border-border/40 pt-3">
            <p className="text-[10px] text-muted-foreground mb-1.5">Recherchierte Fonds — auswählen, um Profil und Vorschläge zu sehen, oder mit den aktuellen Filtern erneut screenen</p>
            <div className="flex flex-wrap gap-1.5" data-testid="fund-list">
              {funds.map(f => (
                <div key={f.id} className={`inline-flex items-center gap-1 border rounded-sm pl-2.5 pr-1 h-8 text-xs transition-colors ${selectedId === f.id ? 'border-amber-500/60 bg-amber-500/10' : 'border-border hover:border-amber-500/40'}`}
                  data-testid={`fund-chip-${f.id}`}>
                  <button onClick={() => setSelectedId(f.id)} className="inline-flex items-center gap-1.5 text-foreground" data-testid={`fund-select-${f.id}`}>
                    {f.status === 'researching' && <Loader2 className="w-3 h-3 animate-spin text-amber-300" />}
                    {f.status === 'failed' && <span className="w-1.5 h-1.5 rounded-full bg-red-400" title={f.error} />}
                    {f.name}
                    {f.screens_count > 0 && <span className="text-[10px] text-muted-foreground font-mono">{f.screens_count}×</span>}
                    {f.deals_count > 0 && <span className="text-[10px] text-amber-300/70 font-mono" title="Deals in der Infra-Fonds-Datenbank">{f.deals_count} Deals</span>}
                  </button>
                  {infraAccess && (
                  <Link to={`/infra-funds/${f.id}`} title="Fonds-Seite mit Deals öffnen" data-testid={`fund-db-${f.id}`}
                    className="w-6 h-6 inline-flex items-center justify-center text-muted-foreground hover:text-amber-300"><Database className="w-3 h-3" /></Link>
                  )}
                  {infraAccess && f.status === 'ready' && (
                    <button onClick={() => rescreen(f.id)} title="Mit aktuellen Filtern erneut screenen" data-testid={`fund-rescreen-${f.id}`}
                      className="w-6 h-6 inline-flex items-center justify-center text-muted-foreground hover:text-amber-300"><Sparkles className="w-3 h-3" /></button>
                  )}
                  {canManage && f.status !== 'researching' && (
                    <button onClick={() => reresearch(f)} title="Recherche aktualisieren" data-testid={`fund-reresearch-${f.id}`}
                      className="w-6 h-6 inline-flex items-center justify-center text-muted-foreground hover:text-sky-300"><RotateCw className="w-3 h-3" /></button>
                  )}
                  {canManage && f.status !== 'researching' && confirmDel !== f.id && (
                    <button onClick={() => setConfirmDel(f.id)} title="Löschen" data-testid={`fund-delete-${f.id}`}
                      className="w-6 h-6 inline-flex items-center justify-center text-muted-foreground hover:text-red-400"><Trash2 className="w-3 h-3" /></button>
                  )}
                  {confirmDel === f.id && (
                    <span className="inline-flex items-center gap-1 pl-1 text-[10px]" data-testid={`fund-delete-confirm-${f.id}`}>
                      <span className="text-red-300">Löschen?</span>
                      <button onClick={() => remove(f)} className="px-1.5 h-5 rounded-sm bg-red-600 hover:bg-red-500 text-white" data-testid={`fund-delete-yes-${f.id}`}>Ja</button>
                      <button onClick={() => setConfirmDel(null)} className="px-1.5 h-5 rounded-sm border border-border hover:bg-secondary text-foreground" data-testid={`fund-delete-no-${f.id}`}>Nein</button>
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {fund && busy && (
          <div className="flex items-center gap-2 text-[11px] text-amber-300/90 border-t border-border/40 pt-3" data-testid="fund-progress">
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
            <span>
              <span className="font-medium text-foreground">{fund.name}</span> — {PHASE_TEXT[phase] || 'läuft…'}
              {phase === 'searching' && fund.research_progress > 0 && ` (${fund.research_progress}/3 Fragen)`}
              {phase === 'scoring' && run && ` ${run.scored || 0}/${run.total_candidates || '…'}`}
            </span>
          </div>
        )}
        {fund?.status === 'failed' && (
          <p className="text-[11px] text-red-400 border-t border-border/40 pt-3" data-testid="fund-error">{fund.error || 'Recherche fehlgeschlagen'}</p>
        )}
        {run?.status === 'failed' && fund?.status === 'ready' && (
          <p className="text-[11px] text-red-400 border-t border-border/40 pt-3" data-testid="fund-run-error">{run.error || 'Screening fehlgeschlagen — bitte erneut screenen'}</p>
        )}
        {fund?.status === 'ready' && <FundProfile fund={fund} />}
      </div>

      {run?.status === 'done' && (
        <div className="bg-card border border-border rounded-sm overflow-hidden" data-testid="fund-results">
          <div className="px-4 py-3 border-b border-border flex items-center justify-between gap-3 flex-wrap">
            <p className="text-xs text-muted-foreground">
              <span className="text-foreground font-medium">{rows.length} Vorschläge</span> für{' '}
              <span className="text-amber-300">{fund?.name}</span>
              {(run.runs_total || 1) > 1 && <span className="text-amber-300/80" data-testid="fund-runs-total"> · aus {run.runs_total} Läufen</span>}
              {(run.runs_total || 1) <= 1 && run.total_hits > rows.length && <span> (Top {rows.length} von {run.total_hits} Treffern)</span>}
              {run.by_angle && (
                <span className="text-muted-foreground/70">
                  {' · '}{Object.entries(run.by_angle).map(([k, v]) => `${v}× ${ANGLE_STYLE[k]?.label || k}`).join(' · ')}
                </span>
              )}
              {filterSummary(run.filters).length > 0 && (
                <span className="text-violet-300/80" data-testid="fund-run-filters"> · Filter: {filterSummary(run.filters, ownerOptions).join(' · ')}</span>
              )}
              {run.pool_size != null && <span className="text-muted-foreground/60"> · {run.pool_size.toLocaleString()} Firmen im Pool</span>}
              {run.screened_total != null && run.pool_size > 0 && <span className="text-muted-foreground/70" data-testid="fund-screened-total"> · Lauf {run.run_no || 1}: {run.screened_total.toLocaleString('de-DE')} von {run.pool_size.toLocaleString('de-DE')} gescreent</span>}
              {run.finished_at && <span className="text-muted-foreground/50"> · {fmtTs(run.finished_at)}</span>}
            </p>
            {infraAccess && (
              <button onClick={() => rescreen(fund.id)} data-testid="fund-rescreen-current-btn"
                className="text-[11px] text-amber-300 hover:text-amber-200 inline-flex items-center gap-1"
                title="Bewertet die nächsten noch nicht gescreenten Firmen (bis zu 300) — bereits gescreente werden übersprungen">
                <Sparkles className="w-3 h-3" /> {run.exhausted ? 'Mit aktuellen Filtern erneut screenen' : 'Nächste 300 mit aktuellen Filtern screenen'}
              </button>
            )}
          </div>
          {run.exhausted && (
            <p className="px-4 py-2 text-[11px] text-amber-300 bg-amber-500/10 border-b border-amber-500/30" data-testid="fund-exhausted">
              Pool erschöpft — alle passenden Firmen wurden in früheren Läufen bereits gescreent. Zurücksetzen oder andere Filter wählen (Infra-Fonds-Seite → „Screening zurücksetzen").
            </p>
          )}
          {rows.length === 0 && (
            <p className="px-4 py-6 text-xs text-muted-foreground text-center" data-testid="fund-empty">
              Keine passende Firma im {run.filters && Object.keys(run.filters).length ? 'gefilterten Pool' : 'Bestand'} gefunden — Filter lockern oder Recherche aktualisieren.
            </p>
          )}
          {rows.map((r, i) => (
            <CompanyRow key={r.id} r={r} i={i} score={r.fund_score} reason={r.fund_reason} prefix="fund"
              badge={<><AngleBadge angle={r.fund_angle} label={r.fund_angle_label} link={r.fund_portfolio_link} />
                {(run.runs_total || 1) > 1 && r.fund_run_no && <span className="text-[10px] font-mono px-1.5 py-0.5 rounded-sm border border-amber-500/30 text-amber-300/80 ml-1" data-testid={`fund-row-run-${i}`}>Lauf {r.fund_run_no}</span>}</>}
              {...rowProps} onOpen={(id) => rowProps.onOpen(id, { fundNote: fundNoteFor(fund?.name, r) })} />
          ))}
        </div>
      )}
      {!fund && funds.length === 0 && (
        <p className="text-[11px] text-muted-foreground/60 px-1 inline-flex items-center gap-1" data-testid="fund-hint">
          <ChevronRight className="w-3 h-3" /> Noch kein Fonds recherchiert — Beispiel: „EQT Infrastructure“, „DIF Capital Partners“, „Antin Infrastructure Partners“.
        </p>
      )}
    </div>
  );
}
